import React, { useEffect, useMemo } from "react";
import {
  UseFormRegister,
  Control,
  FieldErrors,
  Controller,
  useWatch,
  UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";

interface AddressFieldsProps {
  register: UseFormRegister<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
  setValue: UseFormSetValue<any>;
  /** If true, all fields will be read-only/disabled */
  disabled?: boolean;
}

const AddressFields: React.FC<AddressFieldsProps> = ({
  register,
  control,
  errors,
  setValue,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // 1. Load Data from WooCommerce globals
  const allowedCountries = window.checkoutFlowVariables?.allowedCountries || {};
  const countryStates = window.checkoutFlowVariables?.countryStates || {};
  const trDistrictsData = window.checkoutFlowVariables?.trDistricts || [];

  // 2. Watch Country, State and City
  const selectedCountry = useWatch({
    control,
    name: "country",
  });

  const selectedStateCode = useWatch({
    control,
    name: "state",
  });

  const selectedCityName = useWatch({
    control,
    name: "city",
  });

  // 3. Derive Country Options
  const countryOptions = useMemo(() => {
    return Object.entries(allowedCountries).map(([code, name]) => ({
      value: code,
      label: name,
    }));
  }, [allowedCountries]);

  // 4. Auto-select if there is only one country option
  const isSingleCountry = countryOptions.length === 1;
  const singleCountryCode = isSingleCountry
    ? countryOptions[0].value
    : undefined;

  useEffect(() => {
    if (
      isSingleCountry &&
      singleCountryCode &&
      selectedCountry !== singleCountryCode
    ) {
      setValue("country", singleCountryCode, { shouldValidate: true });
      // Clear dependent fields to prevent mismatched state/city data
      setValue("state", "");
      setValue("city", "");
    }
  }, [isSingleCountry, singleCountryCode, selectedCountry, setValue]);

  // 5. Derive State Options based on selected Country
  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    const states = countryStates[selectedCountry] || {};
    return Object.entries(states).map(([code, name]) => ({
      value: code,
      label: name,
    }));
  }, [selectedCountry, countryStates]);

  // 6. Derive District Options based on selected State (Only for TR)
  const districtOptions = useMemo(() => {
    if (selectedCountry !== "TR" || !selectedStateCode) return [];

    // For Turkey, we map State Codes to Names to lookup districts
    const trStates = countryStates["TR"] || {};
    // Lookup the Name (e.g. "Adana") from the Code (e.g. "TR01")
    const selectedStateName = trStates[selectedStateCode];

    if (!selectedStateName) return [];

    // Find the districts using the state name match
    const cityDistricts = trDistrictsData.find(
      (d) => d.name === selectedStateName
    );

    if (!cityDistricts) return [];

    return cityDistricts.districts.map((dist) => ({
      value: dist.name,
      label: dist.name,
    }));
  }, [selectedCountry, selectedStateCode, countryStates, trDistrictsData]);

  // 7. Effect: Programmatic Reset for Districts (TR only)
  useEffect(() => {
    if (disabled || selectedCountry !== "TR") return;
    if (!selectedStateCode || !selectedCityName) return;

    const isValidCity = districtOptions.some(
      (opt) => opt.value === selectedCityName
    );

    if (!isValidCity) {
      setValue("city", "");
    }
  }, [
    selectedCountry,
    selectedStateCode,
    districtOptions,
    selectedCityName,
    setValue,
    disabled,
  ]);

  // Helper to detect if the current country has states (to show Select vs Input)
  const hasStates = stateOptions.length > 0;

  // --- Logic for Postcode Requirement ---
  const isPostcodeRequired = selectedCountry !== "TR";

  return (
    <div className={`checkout-form__section ${disabled ? "is-disabled" : ""}`}>
      <h3 className="checkout-form__section-title">
        {t("checkoutForm.sections.addressInfo", "Address Details")}
      </h3>

      {/* Row 1: Country | State (Province) */}
      <div className="checkout-form__grid">
        {/* Country Select */}
        <div className="form-field">
          <label htmlFor="country" className="required">
            {t("checkoutForm.fields.country", "Country")}
          </label>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                inputId="country"
                className="checkout-form__select-container"
                classNamePrefix="checkout-form-select"
                options={countryOptions}
                placeholder={t(
                  "checkoutForm.placeholders.selectCountry",
                  "Select Country"
                )}
                value={
                  countryOptions.find((c) => c.value === field.value) || null
                }
                // Disable if the entire form is disabled OR if there is only one country option
                isDisabled={disabled || isSingleCountry}
                onChange={(option) => {
                  field.onChange(option?.value);
                  // Reset state and city when country changes
                  setValue("state", "");
                  setValue("city", "");
                }}
              />
            )}
          />
          {errors.country && (
            <p className="form-field__error">
              {errors.country.message as string}
            </p>
          )}
        </div>

        {/* State / Province */}
        <div className="form-field">
          <label htmlFor="state" className="required">
            {t("checkoutForm.fields.state", "State / Province")}
          </label>

          {hasStates ? (
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  inputId="state"
                  className="checkout-form__select-container"
                  classNamePrefix="checkout-form-select"
                  options={stateOptions}
                  placeholder={t(
                    "checkoutForm.placeholders.selectState",
                    "Select State"
                  )}
                  value={
                    stateOptions.find((c) => c.value === field.value) || null
                  }
                  isDisabled={disabled || !selectedCountry}
                  onChange={(option) => {
                    field.onChange(option?.value);
                    if (selectedCountry === "TR") {
                      setValue("city", ""); // Manual reset for TR districts
                    }
                  }}
                />
              )}
            />
          ) : (
            <input
              id="state"
              {...register("state")}
              className={errors.state ? "has-error" : ""}
              disabled={disabled}
              placeholder={t("checkoutForm.placeholders.state", "State")}
            />
          )}

          {errors.state && (
            <p className="form-field__error">
              {errors.state.message as string}
            </p>
          )}
        </div>
      </div>

      {/* Row 2: City/District | Postcode */}
      <div className="checkout-form__grid">
        {/* City / District */}
        <div className="form-field">
          <label htmlFor="city" className="required">
            {selectedCountry === "TR"
              ? t("checkoutForm.fields.district", "District (İlçe)")
              : t("checkoutForm.fields.city", "City")}
          </label>

          {selectedCountry === "TR" ? (
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  inputId="city"
                  className="checkout-form__select-container"
                  classNamePrefix="checkout-form-select"
                  options={districtOptions}
                  placeholder={t(
                    "checkoutForm.placeholders.selectDistrict",
                    "Select District"
                  )}
                  value={
                    districtOptions.find((c) => c.value === field.value) || null
                  }
                  isDisabled={disabled || !selectedStateCode}
                  noOptionsMessage={() =>
                    !selectedStateCode
                      ? t(
                          "checkoutForm.messages.selectStateFirst",
                          "Please select a state first"
                        )
                      : t("checkoutForm.messages.noOptions", "No options")
                  }
                  onChange={(option) => {
                    field.onChange(option?.value);
                  }}
                />
              )}
            />
          ) : (
            <input
              id="city"
              {...register("city")}
              className={errors.city ? "has-error" : ""}
              disabled={disabled}
            />
          )}

          {errors.city && (
            <p className="form-field__error">{errors.city.message as string}</p>
          )}
        </div>

        {/* Postcode */}
        <div className="form-field">
          <label
            htmlFor="postcode"
            className={isPostcodeRequired ? "required" : ""}
          >
            {isPostcodeRequired
              ? t("checkoutForm.fields.postcode", "Postcode")
              : t(
                  "checkoutForm.fields.postcodeOptional",
                  "Postcode (Optional)"
                )}
          </label>
          <input
            id="postcode"
            {...register("postcode")}
            className={errors.postcode ? "has-error" : ""}
            disabled={disabled}
          />
          {errors.postcode && (
            <p className="form-field__error">
              {errors.postcode.message as string}
            </p>
          )}
        </div>
      </div>

      {/* Row 3: Address 1 (Textarea) */}
      <div className="form-field">
        <label htmlFor="address1" className="required">
          {t("checkoutForm.fields.address1", "Address Line 1")}
        </label>
        <textarea
          id="address1"
          {...register("address1")}
          className={`form-field__textarea ${
            errors.address1 ? "has-error" : ""
          }`}
          rows={2}
          placeholder={t(
            "checkoutForm.placeholders.address1",
            "Street, building, apartment number etc."
          )}
          disabled={disabled}
        />
        {errors.address1 && (
          <p className="form-field__error">
            {errors.address1.message as string}
          </p>
        )}
      </div>

      {/* Row 4: Address 2 (Textarea) */}
      <div className="form-field">
        <label htmlFor="address2">
          {t("checkoutForm.fields.address2", "Address Line 2 (Optional)")}
        </label>
        <textarea
          id="address2"
          {...register("address2")}
          className="form-field__textarea"
          rows={2}
          placeholder={t(
            "checkoutForm.placeholders.address2",
            "Additional address info, directions etc."
          )}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default AddressFields;
